<template>
  <div>
    <v-toolbar flat>
      <v-row class="w-100" align="center">
        <v-col cols="4">
          <v-toolbar-title>Session Reporting</v-toolbar-title>
        </v-col>
        <v-col cols="8" class="d-flex justify-end align-center">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            :return-value.sync="dates"
            transition="scale-transition"
            offset-y
            min-width="auto"
            class="dateSession"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="dateRangeText"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                class="mr-2 mt-4"
                dense
                label="Date Range"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="dates"
              no-title
              scrollable
              range
              :max="new Date().toISOString().substr(0, 10)"
            >
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="menu = false">
                Cancel
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="updateTableOnDateChange(dates)"
              >
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>

          <v-select
            v-model="selectedSession"
            label="Select Session"
            :items="sessionOptions"
            item-text="text"
            item-value="id"
            dense
            hide-details
            class="ml-2 selectSession"
            @change="fetchSessionDetails"
          ></v-select>
        </v-col>
      </v-row>
    </v-toolbar>

    <v-container fluid>
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary"
      ></v-progress-circular>

      <template v-else-if="!loading && !error && selectedSessionDetails.length > 0">
        <v-card
          v-for="(session, index) in selectedSessionDetails"
          :key="index"
          class="pa-6 elevation-1 sessionCard mb-4"
        >
          <v-row
            @click="session.expanded = !session.expanded"
            class="clickableRow"
            align="center"
          >
            <v-col cols="3">
              <h3 class="titleHeading">Title</h3>
              <p class="sessionValue">{{ session.title }}</p>
            </v-col>
            <v-col cols="3">
              <h3 class="titleHeading">Date of Session</h3>
              <p class="sessionValue">{{ session.date }}</p>
            </v-col>
            <v-col cols="3">
              <h3 class="titleHeading">Session Start Time</h3>
              <p class="sessionValue">{{ session.startTime }}</p>
            </v-col>
            <v-col cols="2">
              <h3 class="titleHeading">No. of Game in Session</h3>
              <p class="sessionValue">{{ session.gamesPlayed }}</p>
            </v-col>
            <v-col cols="1" class="d-flex justify-end">
              <v-icon>
                {{ session.expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
              </v-icon>
            </v-col>
          </v-row>
          <v-expand-transition>
            <div v-show="session.expanded">
              <v-divider class="my-4"></v-divider>
              <v-row>
                <v-col cols="3">
                  <h3 class="titleHeading">Total Pages Sold</h3>
                  <p class="sessionValue">{{ session.pagesSold }}</p>
                </v-col>
                <v-col cols="3">
                  <h3 class="titleHeading">Total Money In (Revenue)</h3>
                  <p class="sessionValue">{{ session.revenue }}</p>
                </v-col>
                <v-col cols="3">
                  <h3 class="titleHeading">Total Money Out (Payouts/Winnings)</h3>
                  <p class="sessionValue">{{ session.payouts }}</p>
                </v-col>
                <v-col cols="3">
                  <h3 class="titleHeading">Net Profit/Loss</h3>
                  <p
                    class="sessionValue"
                    :class="{
                      redText: session.netProfit < 0,
                      greenText: session.netProfit >= 0,
                    }"
                  >
                    {{ formatNetProfit(session.netProfit) }}
                  </p>
                </v-col>
              </v-row>

              <v-divider class="my-4"></v-divider>

              <v-row>
                <v-col cols="3">
                  <h3 class="titleHeading">Number of Players</h3>
                  <p class="sessionValue">{{ session.players }}</p>
                </v-col>
                <v-col cols="3">
                  <h3 class="titleHeading">Number of Available Patterns</h3>
                  <p class="sessionValue">{{ session.availablePatterns }}</p>
                </v-col>
                <v-col cols="3">
                  <h3 class="titleHeading">Number of Winning Patterns</h3>
                  <p class="sessionValue">{{ session.winningPatterns }}</p>
                </v-col>
                <v-col cols="3">
                  <h3 class="titleHeading">Average Spend Per Head</h3>
                  <p class="sessionValue">{{ session.avgSpend }}</p>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>
        </v-card>
      </template>

      <template v-else-if="!loading && !error && selectedSessionDetails.length === 0">
        <v-card class="pa-6 elevation-1">
          <v-row>
            <v-col class="text-center">
              <p class="sessionValue">No data found for the selected date range.</p>
            </v-col>
          </v-row>
        </v-card>
      </template>

      <template v-else-if="!loading && error">
        <v-card class="pa-6 elevation-1">
          <v-row>
            <v-col class="text-center">
              <p class="sessionValue redText">Error: {{ error }}</p>
            </v-col>
          </v-row>
        </v-card>
      </template>
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { debounce } from "lodash";

export default {
  name: "ReportingSessionHome",
  computed: {
    ...mapGetters("sessionReporting", [
      "sessions",
      "selectedSessionDetails",
      "error",
      "loading",
    ]),
    dateRangeText() {
      return this.dates.length > 0 ? this.dates.join(" ~ ") : "";
    },
    sessionOptions() {
      return this.sessions.map((session) => ({
        text: `${session.title} - ${session.date} ${session.time}`,
        id: session.id,
      }));
    },
  },
  data: () => ({
    debugData: null,
    dates: [
      new Date(new Date().getFullYear(), new Date().getMonth(), 1)
        .toISOString()
        .substr(0, 10),
      new Date().toISOString().substr(0, 10), 
    ],
    menu: false,
    selectedSession: null,
    loading: false,
  }),
  methods: {
    ...mapActions("sessionReporting", ["fetchSessions", "fetchSessionDetails"]),
    formatNetProfit(profit) {
      if (profit === 0) {
        return `€${Math.abs(profit)}`;
      }
      return `${profit > 0 ? "+" : "-"}€${Math.abs(profit)}`;
    },
    updateTableOnDateChange: async function (dates) {
      let selectedDates = dates;
      if (dates.length === 1) {
        selectedDates = [dates[0], dates[0]];
      }

      const today = new Date().toISOString().substr(0, 10);
      selectedDates = selectedDates.map(date => date > today ? today : date);
      this.$refs.menu.save(selectedDates);
      this.dates = selectedDates.sort();
      this.menu = false;
      this.loading = true;
      try {
        await this.fetchSessions({
          startDate: this.dates[0],
          endDate: this.dates[1],
        });
        if (this.sessions.length > 0) {
          this.selectedSession = this.sessions[0].id;
          await this.fetchSessionDetails(this.selectedSession);
        } else {
          this.selectedSession = null;
          
          this.$store.commit("sessionReporting/SET_SESSION_DETAILS", []);
        }
      } catch (error) {
        console.error("Error in updateTableOnDateChange:", error);
      } finally {
        this.loading = false;
      }
    },
    fetchSessionDetails: debounce(async function () {
      if (this.selectedSession) {
        this.loading = true;
        try {
          await this.$store.dispatch(
            "sessionReporting/fetchSessionDetails",
            this.selectedSession
          );
        } catch (error) {
          console.error("Error in fetchSessionDetails:", error);
        } finally {
          this.loading = false;
        }
      } else {
        
        this.$store.commit("sessionReporting/SET_SESSION_DETAILS", []);
      }
    }, 300),
  },
  async created() {
    this.loading = true;
    try {
      const response = await this.fetchSessions({
        startDate: this.dates[0],
        endDate: this.dates[1],
      });
      this.debugData = response; // Store for debugging
      console.log("Sessions fetched:", this.sessions); // Debug log
      if (this.sessions.length > 0) {
        this.selectedSession = this.sessions[0].id;
        const detailsResponse = await this.fetchSessionDetails(
          this.selectedSession
        );
        this.debugData = { sessions: response, details: detailsResponse };
      } else {
        this.selectedSession = null;
        this.$store.commit("sessionReporting/SET_SESSION_DETAILS", []);
      }
    } catch (error) {
      console.error("Error in created hook:", error);
    } finally {
      this.loading = false;
    }
  },
  watch: {
    selectedSession(newVal, oldVal) {
      if (newVal !== oldVal) {
        console.log("Selected session changed to:", newVal);
        this.fetchSessionDetails();
      }
    },
    sessions(newVal) {
      console.log("Sessions updated:", newVal);
      if (newVal.length === 0) {
        this.selectedSession = null;
        this.$store.commit("sessionReporting/SET_SESSION_DETAILS", []);
      }
    },
  },
};
</script>

<style scoped>
.titleHeading {
  color: #323b82;
  font-family: "PT Sans";
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  text-transform: uppercase;
  padding-bottom: 13px;
}

.sessionValue {
  color: #757575;
  font-family: "PT Sans";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-transform: uppercase;
}
.redText {
  color: #ff4d4d !important;
}
.sessionCard {
  border-radius: 2px;
  border: 1px solid #dedede;
  background: #fff;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  cursor: pointer;
}
.clickableRow {
  cursor: pointer;
}
.clickableRow:hover {
  background-color: rgba(0, 0, 0, 0.02);
}
.greenText {
  color: #4caf50 !important;
}
.selectSession {
  width: 280px;
}
.dateSession {
  width: 200px;
}

@media (min-width: 1300px) {
  .selectSession {
    width: 280px;
  }
}
@media (min-width: 700px) and (max-width: 1250px) {
  .selectSession {
    width: 330px;
  }
  .dateSession {
    width: 200px;
  }
}
</style>